/**
 * Header Component - Eliza Forge
 * 
 * Main header bar using DS Toolbar components.
 * Features breadcrumbs, theme toggle, notifications, and user menu.
 */

import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BellIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { ArrowLeft, PanelLeft } from 'lucide-react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '../ui/popover';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications, notificationHelpers } from '../../stores/useNotifications';
import { useMonitorNotificationSync } from '../../hooks/useMonitorNotificationSync';
import NotificationPanel from '../common/NotificationPanel';
import FeedbackPanel from '../common/FeedbackPanel';
import { useUI } from '../../stores/useUI';
import {
  Toolbar,
  ToolbarSection,
  ToolbarDivider,
  ToolbarItem,
  ToolbarTextButton,
} from '../ui/toolbar';
import type { ToolbarSurface } from '../ui/toolbar';
import { Avatar } from '../ui/avatar';
import { BreadcrumbTrail, type BreadcrumbTrailItem } from '../ui/breadcrumb';
import type { NavigationReturnTarget } from './navigationHistory';

interface HeaderProps {
  onMenuToggle?: () => void;
  breadcrumbs?: BreadcrumbTrailItem[];
  returnTarget?: NavigationReturnTarget | null;
  surfaceOverride?: ToolbarSurface;
  homeScrolled?: boolean;
}

export function Header({
  onMenuToggle,
  breadcrumbs = [],
  returnTarget = null,
  surfaceOverride,
  homeScrolled = false,
}: HeaderProps) {
  useMonitorNotificationSync();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const unreadCount = useNotifications(s => s.unreadCount);
  const isNotificationOpen = useNotifications(s => s.isOpen);
  const toggleNotifications = useNotifications(s => s.togglePanel);
  const setNotificationOpen = useNotifications(s => s.setOpen);
  const notificationRef = useRef<HTMLDivElement>(null);
  const theme = useUI(s => s.theme);
  const setTheme = useUI(s => s.setTheme);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close notification panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false);
      }
    }

    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isNotificationOpen, setNotificationOpen]);

  // Initialize sample notifications for demo (only once)
  useEffect(() => {
    const hasInitialized = localStorage.getItem('notifications-demo-initialized');
    if (!hasInitialized && user) {
      notificationHelpers.success(
        'Welcome to Eliza Forge',
        `Welcome back, ${user.first_name || user.full_name}! Your dashboard is ready.`,
        '/admin/dashboard',
        'View Dashboard'
      );

      notificationHelpers.info(
        'System Update Available',
        'A new system update is available with enhanced security features.',
        '/admin/system',
        'View Details'
      );

      notificationHelpers.warning(
        'User Management Access',
        'You now have access to user management features. Review user permissions regularly.',
        '/admin/users',
        'Manage Users'
      );

      localStorage.setItem('notifications-demo-initialized', 'true');
    }
  }, [user]);

  // Simple 2-state theme toggle (light/dark)
  const isDark = theme === 'dark';
  
  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  const handleReturn = () => {
    if (!returnTarget) return;
    navigate(returnTarget.href);
  };

  const isHomePage = location.pathname === '/home';
  const toolbarSurface = isHomePage ? (homeScrolled ? 'home-scrolled' : 'home') : (surfaceOverride ?? 'translucent');

  return (
    <Toolbar
      sticky={!isHomePage}
      surface={toolbarSurface}
      className={isHomePage ? 'absolute inset-x-0 top-0 z-40' : undefined}
      leftContent={
        <ToolbarSection className="min-w-0">
          {onMenuToggle && (
            <>
              <ToolbarItem
                icon={<PanelLeft className="h-5 w-5" strokeWidth={2.1} />}
                onClick={onMenuToggle}
                tooltip="Toggle sidebar"
                aria-label="Toggle sidebar"
              />
              {(returnTarget || breadcrumbs.length > 0) && (
                <ToolbarDivider className="mx-1 h-7 bg-gray-200 dark:bg-dark-border/50" />
              )}
            </>
          )}
          {returnTarget && (
            <>
              <ToolbarTextButton
                icon={<ArrowLeft className="h-4 w-4" strokeWidth={2.1} />}
                onClick={handleReturn}
                aria-label={`Go back to ${returnTarget.label}`}
                title={`Go back to ${returnTarget.label}`}
                className="max-w-[14rem] flex-none text-gray-600 hover:text-charcoal dark:text-gray-300 dark:hover:text-white"
              >
                <span className="truncate">Back to {returnTarget.label}</span>
              </ToolbarTextButton>
              {breadcrumbs.length > 0 && (
                <ToolbarDivider className="mx-1 h-7 bg-gray-200 dark:bg-dark-border/50" />
              )}
            </>
          )}
          {breadcrumbs.length > 0 ? (
            <BreadcrumbTrail
              items={breadcrumbs}
              className="max-w-full"
            />
          ) : null}
        </ToolbarSection>
      }
      rightContent={
        <ToolbarSection>
          {/* Theme Toggle */}
          <ToolbarItem
            icon={isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            onClick={toggleTheme}
            tooltip={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          />

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <ToolbarItem
              icon={<BellIcon className="h-5 w-5" />}
              onClick={toggleNotifications}
              badge={unreadCount > 0 ? unreadCount : undefined}
              tooltip="Notifications"
              aria-label="Notifications"
            />
            <NotificationPanel
              isOpen={isNotificationOpen}
              onClose={() => setNotificationOpen(false)}
            />
          </div>

          <ToolbarDivider />

          {/* Help / Feedback */}
          <ToolbarTextButton
            icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
            onClick={() => setIsFeedbackOpen(true)}
            title="Platform Help & Feedback"
          >
            <span className="hidden md:inline">Help / Feedback</span>
          </ToolbarTextButton>

          {/* Feedback Panel */}
          <FeedbackPanel
            isOpen={isFeedbackOpen}
            onClose={() => setIsFeedbackOpen(false)}
          />

          {/* User Menu */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="cursor-pointer flex items-center gap-1 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-surface-2 transition-colors"
              >
                <Avatar
                  size="sm"
                  name={user?.full_name || user?.first_name || 'User'}
                  className="ring-2 ring-white dark:ring-dark-surface"
                />
                <ChevronDownIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
              </button>
            </PopoverTrigger>

            <PopoverContent align="end" sideOffset={8} className="w-72 overflow-visible p-0">
              {/* User Info */}
              <div className="p-3 border-b border-gray-200 dark:border-dark-border/30">
                <div className="text-sm font-medium text-charcoal dark:text-white">
                  {user?.full_name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Role: {user?.primary_role || 'No role assigned'}
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <Link
                  to="/change-password"
                  className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-2 hover:text-charcoal dark:hover:text-white transition-colors"
                >
                  <KeyIcon className="h-4 w-4 mr-3" aria-hidden="true" />
                  Change Password
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-2 hover:text-charcoal dark:hover:text-white transition-colors"
                >
                  <UserCircleIcon className="h-4 w-4 mr-3" />
                  Profile Settings
                </Link>

                <Link
                  to="/settings"
                  className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-2 hover:text-charcoal dark:hover:text-white transition-colors"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-3" />
                  Settings
                </Link>
              </div>

              {/* Sign Out */}
              <div className="py-1 border-t border-gray-200 dark:border-dark-border/30">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-3 py-2 text-sm text-left text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-2 hover:text-charcoal dark:hover:text-white transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                  Sign Out
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </ToolbarSection>
      }
    />
  );
}

export default Header;
